import { Component, forwardRef, Input } from '@angular/core';
import { FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessor } from '@angular/forms/src/directives';

import { DynamicTranslationService } from '../../../dynamic-translation/dynamic-translation.service';
import { IpChecker } from '../ip-checker/ip-checker';
import { IPErrorMessages } from './ip-address-error-messages';
import { CssWidths } from '../../../utils/css-widths.enum';

/**
 * __Usage__:
 * ```html
 * Template driven Form
 * <ls-ip-address
 *  [label]="'IP Address'"
 *  [placeHolder]="'192.168.0.1'"
 *  [required]="false"
 *  [name]="'IPAddress'"
 *  ngModel>
 * </ls-ip-address>
 * ```
 * ```html
 * Reactive Forms
 * <ls-ip-address
 *  [label]="'IP Address'"
 *  [placeHolder]="'192.168.0.1'"
 *  [required]="false"
 *  formControlName="IPAddress">
 * </ls-ip-address>
 * ```
 */
@Component({
  selector: 'ls-ip-address',
  templateUrl: './ip-address.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IpAddressComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => IpAddressComponent),
      multi: true
    }
  ]
})
export class IpAddressComponent implements ControlValueAccessor {
  @Input()
  public label = '';
  @Input()
  public address = '';
  @Input()
  public placeHolder = '';
  @Input()
  public required = false;
  @Input()
  public width: CssWidths = CssWidths.HUNDRED;
  public errorMessage: string = IPErrorMessages.NoError;

  constructor(private translationService: DynamicTranslationService) {}

  public writeValue(value: string): void {
    value !== null ? (this.address = value) : (this.address = '');
  }

  public registerOnChange(fn: Function): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(): void {
    return;
  }

  public propagateChange: any = () => {
    return;
  };

  public validate: any = (control: FormControl) => {
    if (!control.dirty) {
      this.setErrorMessage(IPErrorMessages.NoError);
      return null;
    }

    if (this.required && this.address.length === 0) {
      this.setErrorMessage(IPErrorMessages.RequiredField);
      return { valid: false };
    } else if (!IpChecker.isValidIp(this.address) && this.address.length > 0) {
      this.setErrorMessage(IPErrorMessages.InvalidIP);
      return { valid: false };
    } else {
      this.setErrorMessage(IPErrorMessages.NoError);
      return null;
    }
  };

  public ipAddressChanged($event: any): void {
    if ($event.target.value !== this.address) {
      this.address = $event.target.value;
      this.propagateChange(this.address);
    }
  }

  private setErrorMessage(message: string): void {
    this.errorMessage = this.translationService.translate(message);
  }
}
